import { createAsyncTypes } from 'store/utils';

// State Key
export const KEY = 'clientIntegrations';

// Action Types
export const types = {
  GET_CLIENT_INTEGRATIONS: createAsyncTypes(`${KEY}/GET_CLIENT_INTEGRATIONS`),
  CREATE_CLIENT_INTEGRATION: createAsyncTypes(`${KEY}/CREATE_CLIENT_INTEGRATION`),
  UPDATE_CLIENT_INTEGRATION: createAsyncTypes(`${KEY}/UPDATE_CLIENT_INTEGRATION`),
  DELETE_CLIENT_INTEGRATION: createAsyncTypes(`${KEY}/DELETE_CLIENT_INTEGRATION`),
  GET_TICKETING_EVENTS: createAsyncTypes(`${KEY}/GET_TICKETING_EVENTS`),
  CREATE_CUSTOMER: createAsyncTypes(`${KEY}/CREATE_CUSTOMER`),
  GET_PAYMENT_SETTINGS: createAsyncTypes(`${KEY}/GET_PAYMENT_SETTINGS`),
  UPDATE_PAYMENT_SETTINGS: createAsyncTypes(`${KEY}/UPDATE_PAYMENT_SETTINGS`),
  GET_PRODUCTS: createAsyncTypes(`${KEY}/GET_PRODUCTS`),
  SYNC_PRODUCTS: createAsyncTypes(`${KEY}/SYNC_PRODUCTS`),
  TEST_ACCESS: createAsyncTypes(`${KEY}/TEST_ACCESS`),
  RESET_EVENTS: `${KEY}/RESET_EVENTS`,
  RESET_CUSTOMER: `${KEY}/RESET_CUSTOMER`,
  RESET_API_ACCESS: `${KEY}/RESET_API_ACCESS`,
  RESET_ACTION: `${KEY}/RESET_ACTION`,
  RESET_ERROR: `${KEY}/RESET_ERROR`
};

export interface ClientIntegration {
  id: string;
  createdAt: string;
  updatedAt: string;
  clientId: string;
  baseUrl: string;
  name: string;
  integration: {
    id: string;
    name: string;
    code: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  configuration?: any;
  cityPass?: {
    baseUrl: string;
    username: string;
    password: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    integrationSettingsList?: any;
  };
}

export interface Event {
  id: string;
  name: string;
  venue: string;
  date: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detailsParams: any;
  genre?: string;
  type?: string;
}

export interface EventDetails extends Event {
  availability: number;
  // Not worried about all the offers at the moment
}

export interface PaymentSettings {
  venueTaxes: {
    label: string;
    venueId: string;
    percentage: number;
  }[];
  gatewayCode: string;
  allowRefunds: boolean;
  gatewayDetails: {
    apiKey: string | null;
    secretKey: string | null;
    terminalId: string | null;
  };
  kisTransactionFees: {
    amount: number | null;
    percentage: number | null;
  }[];
  defaultKisProductFee: {
    amount: number | null;
    percentage: number | null;
  };
  isKisMerchantAccount: boolean;
  cardProcessingFeeMarkup: {
    amount: number | null;
    percentage: number | null;
  };
}

export interface ClientIntegrationsState {
  readonly events: Event[];
  readonly clientIntegrations: ClientIntegration[];
  readonly customer: {
    customerId: string;
    isCreated: boolean;
  } | null;
  readonly paymentSettings: PaymentSettings | null;
  readonly products: Products | null;
  readonly hasTransactions: boolean;
  readonly apiAccess: boolean;
  readonly action: {
    readonly pending: boolean;
    readonly success: boolean;
    readonly error: string | null;
  };
}

export interface CreateClientIntegrationPayload {
  name: string;
  baseUrl: string;
  integrationId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  configuration?: any;
}

export interface CreateCustomerPayload {
  id: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  details: any;
}

export interface PriceType {
  id?: string;
  name: string;
  price: number;
  ticketTemplateId: string | null;
  enabled: boolean;
}

export interface Product {
  id?: string;
  name: string;
  ticketTemplateId: string | null;
  enabled: boolean;
  fees: {
    amount: number | null;
    percentage: number | null;
  }[];
  priceTypes: PriceType[];
}

export interface Products {
  products: Product[];
  orderTicketTemplateId: string | null;
}

export interface GetPaymentSettingsRequest {
  type: typeof types.GET_PAYMENT_SETTINGS.REQUEST;
  payload: string;
}

export interface GetPaymentSettingsPending {
  type: typeof types.GET_PAYMENT_SETTINGS.PENDING;
  payload: boolean;
}

export interface GetPaymentSettingsSuccess {
  type: typeof types.GET_PAYMENT_SETTINGS.SUCCESS;
  payload: GetPaymentSettingsResponse;
}

export interface GetPaymentSettingsError {
  type: typeof types.GET_PAYMENT_SETTINGS.ERROR;
  payload: string;
}

export interface UpdatePaymentSettingsPayload {
  id: string;
  data: PaymentSettings;
}

export interface UpdatePaymentSettingsRequest {
  type: typeof types.UPDATE_PAYMENT_SETTINGS.REQUEST;
  payload: UpdatePaymentSettingsPayload;
}

export interface UpdatePaymentSettingsPending {
  type: typeof types.UPDATE_PAYMENT_SETTINGS.PENDING;
  payload: boolean;
}

export interface UpdatePaymentSettingsSuccess {
  type: typeof types.UPDATE_PAYMENT_SETTINGS.SUCCESS;
}

export interface UpdatePaymentSettingsError {
  type: typeof types.UPDATE_PAYMENT_SETTINGS.ERROR;
  payload: string;
}

export interface GetClientIntegrationsRequest {
  type: typeof types.GET_CLIENT_INTEGRATIONS.REQUEST;
}

export interface GetClientIntegrationsPending {
  type: typeof types.GET_CLIENT_INTEGRATIONS.PENDING;
  payload: boolean;
}

export interface GetClientIntegrationsSuccess {
  type: typeof types.GET_CLIENT_INTEGRATIONS.SUCCESS;
  payload: ClientIntegration[];
}

export interface GetClientIntegrationsError {
  type: typeof types.GET_CLIENT_INTEGRATIONS.ERROR;
  payload: string;
}

export interface CreateClientIntegrationRequest {
  type: typeof types.CREATE_CLIENT_INTEGRATION.REQUEST;
  payload: CreateClientIntegrationPayload;
}

export interface CreateClientIntegrationPending {
  type: typeof types.CREATE_CLIENT_INTEGRATION.PENDING;
  payload: boolean;
}

export interface CreateClientIntegrationSuccess {
  type: typeof types.CREATE_CLIENT_INTEGRATION.SUCCESS;
}

export interface CreateClientIntegrationError {
  type: typeof types.CREATE_CLIENT_INTEGRATION.ERROR;
  payload: string;
}

export type UpdateClientIntegrationPayload = Partial<ClientIntegration> & { id: string };

export interface UpdateClientIntegrationRequest {
  type: typeof types.UPDATE_CLIENT_INTEGRATION.REQUEST;
  payload: UpdateClientIntegrationPayload;
}

export interface UpdateClientIntegrationPending {
  type: typeof types.UPDATE_CLIENT_INTEGRATION.PENDING;
  payload: boolean;
}

export interface UpdateClientIntegrationSuccess {
  type: typeof types.UPDATE_CLIENT_INTEGRATION.SUCCESS;
  payload: ClientIntegration;
}

export interface UpdateClientIntegrationError {
  type: typeof types.UPDATE_CLIENT_INTEGRATION.ERROR;
  payload: string;
}

export interface DeleteClientIntegrationRequest {
  type: typeof types.DELETE_CLIENT_INTEGRATION.REQUEST;
  payload: { id: string };
}

export interface DeleteClientIntegrationPending {
  type: typeof types.DELETE_CLIENT_INTEGRATION.PENDING;
  payload: boolean;
}

export interface DeleteClientIntegrationSuccess {
  type: typeof types.DELETE_CLIENT_INTEGRATION.SUCCESS;
}

export interface DeleteClientIntegrationError {
  type: typeof types.DELETE_CLIENT_INTEGRATION.ERROR;
  payload: string;
}

export interface GetTicketingEventsRequest {
  type: typeof types.GET_TICKETING_EVENTS.REQUEST;
  payload: string;
}

export interface GetTicketingEventsPending {
  type: typeof types.GET_TICKETING_EVENTS.PENDING;
  payload: boolean;
}

export interface GetTicketingEventsSuccess {
  type: typeof types.GET_TICKETING_EVENTS.SUCCESS;
  payload: Event[];
}

export interface GetTicketingEventsError {
  type: typeof types.GET_TICKETING_EVENTS.ERROR;
  payload: string;
}

export interface CreateCustomerResponse {
  customerId: string;
  isCreated: boolean;
}

export interface CreateCustomerRequest {
  type: typeof types.CREATE_CUSTOMER.REQUEST;
  payload: CreateCustomerPayload;
}

export interface CreateCustomerSuccess {
  type: typeof types.CREATE_CUSTOMER.SUCCESS;
  payload: CreateCustomerResponse;
}

export interface CreateCustomerPending {
  type: typeof types.CREATE_CUSTOMER.PENDING;
  payload: boolean;
}

export interface CreateCustomerError {
  type: typeof types.CREATE_CUSTOMER.ERROR;
  payload: string;
}

export interface TestAccessRequest {
  type: typeof types.TEST_ACCESS.REQUEST;
  payload: string;
}

export interface TestAccessPending {
  type: typeof types.TEST_ACCESS.PENDING;
  payload: boolean;
}

export interface TestAccessError {
  type: typeof types.TEST_ACCESS.ERROR;
  payload: string;
}

export interface ResetEvents {
  type: typeof types.RESET_EVENTS;
}

export interface ResetAction {
  type: typeof types.RESET_ACTION;
}

export interface ResetError {
  type: typeof types.RESET_ERROR;
}

export interface GetPaymentSettingsResponse {
  paymentSettings: PaymentSettings;
  hasTransactions: boolean;
}

export interface GetProductsRequest {
  type: typeof types.GET_PRODUCTS.REQUEST;
  payload: string; // clientIntegrationId
}

export interface GetProductsPending {
  type: typeof types.GET_PRODUCTS.PENDING;
  payload: boolean;
}

export interface GetProductsSuccess {
  type: typeof types.GET_PRODUCTS.SUCCESS;
  payload: Products;
}

export interface GetProductsError {
  type: typeof types.GET_PRODUCTS.ERROR;
  payload: string;
}

export interface SyncProductsPayload {
  id: string; // clientIntegrationId
  data: {
    products: Product[];
    orderTicketTemplateId: string | null;
  };
}

export interface SyncProductsRequest {
  type: typeof types.SYNC_PRODUCTS.REQUEST;
  payload: SyncProductsPayload;
}

export interface SyncProductsPending {
  type: typeof types.SYNC_PRODUCTS.PENDING;
  payload: boolean;
}

export interface SyncProductsSuccess {
  type: typeof types.SYNC_PRODUCTS.SUCCESS;
  payload: Products;
}

export interface SyncProductsError {
  type: typeof types.SYNC_PRODUCTS.ERROR;
  payload: string;
}

export type ClientIntegrationsActionTypes =
  | GetClientIntegrationsRequest
  | GetClientIntegrationsPending
  | GetClientIntegrationsSuccess
  | GetClientIntegrationsError
  | CreateClientIntegrationRequest
  | CreateClientIntegrationPending
  | CreateClientIntegrationSuccess
  | CreateClientIntegrationError
  | UpdateClientIntegrationRequest
  | UpdateClientIntegrationPending
  | UpdateClientIntegrationSuccess
  | UpdateClientIntegrationError
  | DeleteClientIntegrationRequest
  | DeleteClientIntegrationPending
  | DeleteClientIntegrationSuccess
  | DeleteClientIntegrationError
  | GetTicketingEventsRequest
  | GetTicketingEventsPending
  | GetTicketingEventsSuccess
  | GetTicketingEventsError
  | GetPaymentSettingsRequest
  | GetPaymentSettingsPending
  | GetPaymentSettingsSuccess
  | GetPaymentSettingsError
  | UpdatePaymentSettingsRequest
  | UpdatePaymentSettingsPending
  | UpdatePaymentSettingsSuccess
  | UpdatePaymentSettingsError
  | GetProductsRequest
  | GetProductsPending
  | GetProductsSuccess
  | GetProductsError
  | SyncProductsRequest
  | SyncProductsPending
  | SyncProductsSuccess
  | SyncProductsError
  | CreateCustomerRequest
  | CreateCustomerPending
  | CreateCustomerSuccess
  | CreateCustomerError
  | TestAccessRequest
  | TestAccessPending
  | TestAccessError
  | ResetEvents
  | ResetAction
  | ResetError;
